(() => {
  const MANAGER_SELECTOR = 'vl-product-compare-manager';
  const MAX_PRODUCTS = 3;

  function escapeHtml(value) {
    return String(value ?? '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#039;');
  }

  function formatSpecValueHtml(value) {
    const lines = String(value ?? '')
      .split(/\s*\|\|\s*|\r?\n/)
      .map((line) => line.trim())
      .filter(Boolean);
    return lines.map(escapeHtml).join('<br>');
  }

  function comparableSpecValue(value) {
    return String(value ?? '')
      .replace(/\s*(?:\|\||\r?\n)\s*/g, '\n')
      .trim()
      .toLowerCase();
  }

  function formatMoney(value, currency) {
    if (value == null || value === '') return '';
    const amount = Number(value) / 100;
    const hasFraction = Math.round(amount * 100) % 100 !== 0;
    return new Intl.NumberFormat(document.documentElement.lang || 'en', {
      style: 'currency',
      currency: currency || 'USD',
      minimumFractionDigits: hasFraction ? 2 : 0,
      maximumFractionDigits: hasFraction ? 2 : 0
    }).format(amount);
  }

  function variantImage(variant) {
    return variant?.featured_image?.src || variant?.featured_image || '';
  }

  function productVariantUrl(productState) {
    const handle = productState.product?.handle || '';
    const variantId = productState.variant?.id;
    return `/products/${handle}${variantId ? `?variant=${encodeURIComponent(variantId)}` : ''}`;
  }

  function domToken(value) {
    return String(value || 'specifications').toLowerCase().replace(/[^a-z0-9_-]+/g, '-');
  }

  function swatchBackground(value, patterns = '') {
    const colorName = String(value || '').trim();
    const configuredValue = String(patterns).split(/\r?\n/).reduce((match, pattern) => {
      const [label, ...parts] = pattern.split('::');
      return label?.trim().toLowerCase() === colorName.toLowerCase()
        ? parts.join('::').trim()
        : match;
    }, '');
    const swatchValue = configuredValue || colorName;

    if (/\.(png|jpe?g|webp)$/i.test(swatchValue)) {
      return `url("/cdn/shop/files/${encodeURIComponent(swatchValue)}")`;
    }

    const colors = swatchValue.split('/').map((color) => color.trim()).filter((color) => (
      window.CSS?.supports?.('color', color)
    ));
    if (colors.length > 1) {
      const legacyStops = {
        2: ['-6.77%', '85.41%'],
        3: ['15.26%', '49.98%', '84.7%'],
        5: ['25.68%', '36.63%', '52.32%', '64%', '74.29%']
      }[colors.length];
      if (legacyStops) {
        return `linear-gradient(44deg, ${colors.map((color, index) => (
          `${color} ${legacyStops[index]}`
        )).join(', ')})`;
      }
      const step = 100 / (colors.length - 1);
      return `linear-gradient(44deg, ${colors.map((color, index) => (
        `${color} ${index * step}%`
      )).join(', ')})`;
    }
    return colors[0] || '#d9d9d9';
  }

  function colorValuesForSelection(productState, colorIndex) {
    const variants = Array.isArray(productState.product?.variants)
      ? productState.product.variants
      : [];
    const selectedOptionFields = ['style', 'size'].filter((field) => (
      productState.optionIndexes?.[field] != null && productState.selection?.[field] != null
    ));

    return Array.from(new Set(variants
      .filter((variant) => selectedOptionFields.every((field) => (
        variant.options?.[productState.optionIndexes[field]] === productState.selection[field]
      )))
      .map((variant) => variant.options?.[colorIndex])
      .filter(Boolean)));
  }

  function renderOptionGroups(productState, fields = productState.optionSchema) {
    const { product, optionSchema, selection } = productState;
    return fields.map((field) => {
      const index = optionSchema.indexOf(field);
      const optionIndex = productState.optionIndexes?.[field] ?? index;
      const option = product.options?.[optionIndex];
      const label = typeof option === 'string' ? option : option?.name || field;
      const values = field === 'color'
        ? colorValuesForSelection(productState, optionIndex)
        : Array.from(new Set(
          (product.variants || []).map((variant) => variant.options?.[optionIndex]).filter(Boolean)
        ));

      if (field === 'color') {
        return `
          <fieldset class="vl-product-compare__option-group vl-product-compare__option-group--color" data-option-field="${escapeHtml(field)}">
            <legend>${escapeHtml(label)}</legend>
            <div class="vl-product-compare__color-values">
              ${values.map((value) => `
                <label class="vl-product-compare__color-value" title="${escapeHtml(value)}">
                  <input
                    type="radio"
                    name="compare-${escapeHtml(productState.id)}-${escapeHtml(field)}"
                    value="${escapeHtml(value)}"
                    data-vl-product-compare-option
                    data-product-id="${escapeHtml(productState.id)}"
                    data-option-field="${escapeHtml(field)}"
                    ${selection[field] === value ? 'checked' : ''}
                  >
                  <span style="--vl-compare-swatch: ${escapeHtml(swatchBackground(value, productState.swatchPatterns))}"></span>
                  <span class="visually-hidden">${escapeHtml(value)}</span>
                </label>
              `).join('')}
            </div>
          </fieldset>
        `;
      }

      return `
        <label class="vl-product-compare__option-select">
          <span class="vl-product-compare__option-label">${escapeHtml(label)}</span>
          <span class="vl-product-compare__select-wrap">
            <span class="vl-product-compare__select-value" aria-hidden="true">${escapeHtml(selection[field] || values[0] || '')}</span>
            <span class="vl-product-compare__select-chevron" aria-hidden="true">
              <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
                <path d="M9.71886 13.702C9.88158 13.8647 10.1453 13.8647 10.3081 13.702L17.0846 6.92546C17.2472 6.76275 17.2472 6.49895 17.0846 6.33627L16.4954 5.74708C16.3327 5.5844 16.0689 5.58448 15.9062 5.74708L10.0135 11.6398L4.12071 5.74708C3.95799 5.5844 3.69422 5.58437 3.53152 5.74708L2.94233 6.33627C2.77963 6.49897 2.77966 6.76274 2.94233 6.92546L9.71886 13.702Z" fill="#1F1F1F"/>
              </svg>
            </span>
            <select
              data-vl-product-compare-option
              data-product-id="${escapeHtml(productState.id)}"
              data-option-field="${escapeHtml(field)}"
              aria-label="${escapeHtml(label)}"
            >
              ${values.map((value) => `<option value="${escapeHtml(value)}" ${selection[field] === value ? 'selected' : ''}>${escapeHtml(value)}</option>`).join('')}
            </select>
          </span>
        </label>
      `;
    }).join('');
  }

  function renderChoiceOptions(productState, labels) {
    const choiceFields = productState.optionSchema.filter((field) => field !== 'color');
    if (choiceFields.length) return renderOptionGroups(productState, choiceFields);
    if (!productState.optionSchema.includes('color')) return '';

    return `
      <span class="vl-product-compare__option-select vl-product-compare__option-select--static">
        <span class="vl-product-compare__select-wrap vl-product-compare__select-wrap--static">
          <span class="vl-product-compare__select-value">${escapeHtml(labels.oneSize || 'One Size')}</span>
        </span>
      </span>
    `;
  }

  function renderProductImage(productState, index, linkToProduct) {
    const image = variantImage(productState.variant);
    if (!image) return '';

    const imageAlt = linkToProduct ? '' : productState.fullName;
    const imageMarkup = `<img src="${escapeHtml(image)}" alt="${escapeHtml(imageAlt)}" loading="${index === 0 ? 'eager' : 'lazy'}" decoding="async" data-vl-product-compare-image>`;
    if (!linkToProduct) return imageMarkup;

    return `
      <a
        class="vl-product-compare__image-link"
        href="${escapeHtml(productVariantUrl(productState))}"
        aria-hidden="true"
        tabindex="-1"
      >
        ${imageMarkup}
      </a>
    `;
  }

  function renderProduct(productState, index, labels, linkImageToProduct) {
    const { variant } = productState;
    const currency = window.Shopify?.currency?.active || 'USD';

    return `
      <article class="vl-product-compare__product" data-product-id="${escapeHtml(productState.id)}">
        <div class="vl-product-compare__product-primary">
          <div class="vl-product-compare__image-frame">
            ${renderProductImage(productState, index, linkImageToProduct)}
          </div>
          <h2 class="visually-hidden">${escapeHtml(productState.fullName)}</h2>
          <div class="vl-product-compare__options vl-product-compare__options--choices">${renderChoiceOptions(productState, labels)}</div>
        </div>
        <div class="vl-product-compare__product-colors">
          <div class="vl-product-compare__options vl-product-compare__options--colors">${renderOptionGroups(productState, productState.optionSchema.filter((field) => field === 'color'))}</div>
        </div>
        <div class="vl-product-compare__product-commerce">
          <div class="vl-product-compare__product-commerce-copy">
            <div class="vl-product-compare__variant-price">
              <span>${escapeHtml(formatMoney(variant?.price, currency))}</span>
              ${Number(variant?.compare_at_price || 0) > Number(variant?.price || 0)
                ? `<s>${escapeHtml(formatMoney(variant.compare_at_price, currency))}</s>`
                : ''}
            </div>
            ${variant?.price ? `<p class="vl-product-compare__financing affirm-as-low-as" data-page-type="product" data-amount="${escapeHtml(variant.price)}"></p>` : ''}
            ${variant?.sku ? `<p class="vl-product-compare__sku visually-hidden">SKU: ${escapeHtml(variant.sku)}</p>` : ''}
            ${variant?.available === false ? `<p class="vl-product-compare__availability">${escapeHtml(labels.unavailable || 'Unavailable')}</p>` : ''}
          </div>
          <a class="vl-product-compare__explore" href="${escapeHtml(productVariantUrl(productState))}">
            ${escapeHtml(labels.exploreEbike || 'Explore EBike')}
            <span class="visually-hidden">: ${escapeHtml(productState.fullName)}</span>
          </a>
        </div>
      </article>
    `;
  }

  function renderEmptyProduct(index, labels) {
    return `
      <article class="vl-product-compare__product vl-product-compare__product--empty">
        <button type="button" class="vl-product-compare__empty-button" data-vl-product-compare-selector data-slot-index="${index}" aria-haspopup="dialog">
          <span aria-hidden="true">
            <svg viewBox="0 0 20 20" fill="none">
              <path d="M10 2.5v15M2.5 10h15" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
          </span>
          <span class="visually-hidden">${escapeHtml(labels.selectThirdEbike || 'Select a third ebike to compare')}</span>
        </button>
      </article>
    `;
  }

  function slotsFor(products) {
    const slots = [...products];
    while (slots.length < MAX_PRODUCTS) slots.push(null);
    return slots;
  }

  function renderProducts(manager, products) {
    const productsContainer = manager.querySelector('[data-vl-product-compare-products]');
    const selectorsContainer = manager.querySelector('[data-vl-product-compare-selectors]');
    const stickyActionsContainer = manager.querySelector('[data-vl-product-compare-sticky-actions]');
    if (!productsContainer || !selectorsContainer) return;
    const slots = slotsFor(products);

    selectorsContainer.innerHTML = slots.map((product, index) => {
      const fixedPdpProduct = manager.mode === 'pdp' && index === 0;
      return `
      <button type="button" class="${product ? '' : 'vl-product-compare__selector--empty'}" data-vl-product-compare-selector data-slot-index="${index}" aria-haspopup="dialog" ${fixedPdpProduct ? 'disabled aria-disabled="true"' : ''}>
        <span class="vl-product-compare__selector-title" title="${escapeHtml(product?.shortName || manager.labels?.selectEbike || 'Select Ebike')}">${escapeHtml(product?.shortName || manager.labels?.selectEbike || 'Select Ebike')}</span>
        ${fixedPdpProduct ? '' : `<span class="vl-product-compare__selector-chevron" aria-hidden="true">
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M9.71886 13.702C9.88158 13.8647 10.1453 13.8647 10.3081 13.702L17.0846 6.92546C17.2472 6.76275 17.2472 6.49895 17.0846 6.33627L16.4954 5.74708C16.3327 5.5844 16.0689 5.58448 15.9062 5.74708L10.0135 11.6398L4.12071 5.74708C3.95799 5.5844 3.69422 5.58437 3.53152 5.74708L2.94233 6.33627C2.77963 6.49897 2.77966 6.76274 2.94233 6.92546L9.71886 13.702Z" fill="#1F1F1F"/>
          </svg>
        </span>`}
      </button>
    `;
    }).join('');
    productsContainer.innerHTML = slots.map((product, index) => (
      product
        ? renderProduct(product, index, manager.labels || {}, manager.mode === 'page')
        : renderEmptyProduct(index, manager.labels || {})
    )).join('');
    if (stickyActionsContainer) {
      stickyActionsContainer.innerHTML = slots.map((product) => (
        product
          ? `<a class="vl-product-compare__sticky-explore" href="${escapeHtml(productVariantUrl(product))}">
              ${escapeHtml(manager.labels?.exploreEbike || 'Explore EBike')}
              <span class="visually-hidden">: ${escapeHtml(product.fullName)}</span>
            </a>`
          : '<span class="vl-product-compare__sticky-explore-placeholder" aria-hidden="true"></span>'
      )).join('');
    }
  }

  function updateVariantProduct(manager, products, productId, optionField) {
    const index = products.findIndex((product) => String(product.id) === String(productId));
    const productsContainer = manager.querySelector('[data-vl-product-compare-products]');
    const productState = products[index];
    const productElement = productsContainer?.children[index];
    if (!productState || !productElement || productElement.dataset.productId !== String(productId)) {
      return { updated: false, affirmChanged: false };
    }

    const activeOption = document.activeElement?.closest?.('[data-vl-product-compare-option]');
    const restoreOptionFocus = activeOption?.dataset.productId === String(productId);
    const { variant } = productState;
    const image = variantImage(variant);
    const imageFrame = productElement.querySelector('.vl-product-compare__image-frame');
    const currentImage = imageFrame?.querySelector('[data-vl-product-compare-image]');
    const productUrl = productVariantUrl(productState);
    if (imageFrame) {
      imageFrame.classList.remove('has-image-error');
      const imageLink = imageFrame.querySelector('.vl-product-compare__image-link');
      if (imageLink) imageLink.href = productUrl;
      if (image) {
        if (currentImage) {
          currentImage.src = image;
          currentImage.alt = manager.mode === 'page' ? '' : (productState.fullName || '');
          currentImage.hidden = false;
        } else {
          imageFrame.innerHTML = renderProductImage(
            productState,
            index,
            manager.mode === 'page'
          );
        }
      } else {
        imageFrame.replaceChildren();
      }
    }

    const exploreLink = productElement.querySelector('.vl-product-compare__explore');
    if (exploreLink) exploreLink.href = productUrl;
    const stickyExploreLink = manager
      .querySelector('[data-vl-product-compare-sticky-actions]')
      ?.children[index];
    if (stickyExploreLink?.matches('.vl-product-compare__sticky-explore')) {
      stickyExploreLink.href = productUrl;
    }

    const choiceOptions = productElement.querySelector('.vl-product-compare__options--choices');
    const colorOptions = productElement.querySelector('.vl-product-compare__options--colors');
    if (choiceOptions) {
      choiceOptions.innerHTML = renderChoiceOptions(productState, manager.labels || {});
    }
    if (colorOptions) {
      colorOptions.innerHTML = renderOptionGroups(
        productState,
        productState.optionSchema.filter((field) => field === 'color')
      );
    }

    const currency = window.Shopify?.currency?.active || 'USD';
    const price = productElement.querySelector('.vl-product-compare__variant-price');
    if (price) {
      price.innerHTML = `
        <span>${escapeHtml(formatMoney(variant?.price, currency))}</span>
        ${Number(variant?.compare_at_price || 0) > Number(variant?.price || 0)
          ? `<s>${escapeHtml(formatMoney(variant.compare_at_price, currency))}</s>`
          : ''}
      `;
    }

    const commerceCopy = productElement.querySelector('.vl-product-compare__product-commerce-copy');
    let financing = productElement.querySelector('.vl-product-compare__financing');
    let affirmChanged = false;
    const amount = variant?.price ? String(variant.price) : '';
    if (amount) {
      if (!financing) {
        financing = document.createElement('p');
        financing.className = 'vl-product-compare__financing affirm-as-low-as';
        financing.dataset.pageType = 'product';
        const nextCommerceDetail = commerceCopy?.querySelector('.vl-product-compare__sku, .vl-product-compare__availability');
        if (nextCommerceDetail) nextCommerceDetail.before(financing);
        else commerceCopy?.append(financing);
        affirmChanged = true;
      }
      if (financing.dataset.amount !== amount) {
        financing.dataset.amount = amount;
        affirmChanged = true;
      }
    } else if (financing) {
      financing.remove();
    }

    let sku = productElement.querySelector('.vl-product-compare__sku');
    if (variant?.sku) {
      if (!sku) {
        sku = document.createElement('p');
        sku.className = 'vl-product-compare__sku visually-hidden';
        const availabilityDetail = commerceCopy?.querySelector('.vl-product-compare__availability');
        if (availabilityDetail) availabilityDetail.before(sku);
        else commerceCopy?.append(sku);
      }
      sku.textContent = `SKU: ${variant.sku}`;
    } else {
      sku?.remove();
    }

    let availability = productElement.querySelector('.vl-product-compare__availability');
    if (variant?.available === false) {
      if (!availability) {
        availability = document.createElement('p');
        availability.className = 'vl-product-compare__availability';
        commerceCopy?.append(availability);
      }
      availability.textContent = manager.labels?.unavailable || 'Unavailable';
    } else {
      availability?.remove();
    }

    if (restoreOptionFocus) {
      const nextOption = Array.from(productElement.querySelectorAll('[data-vl-product-compare-option]')).find((input) => (
        input.dataset.optionField === optionField && (input.tagName === 'SELECT' || input.checked)
      ));
      nextOption?.focus({ preventScroll: true });
    }

    return { updated: true, affirmChanged };
  }

  function normalizedSpecValue(value, definition, labels) {
    if (value == null || value === '') return labels.notAvailable || '—';
    if (typeof value === 'boolean') return value ? (labels.yes || 'Yes') : (labels.no || 'No');
    const formattedValue = String(value).trim();
    const unit = String(definition.unit || '').trim();
    if (!unit || formattedValue.toLowerCase().endsWith(unit.toLowerCase())) return formattedValue;
    return `${formattedValue} ${unit}`;
  }

  function specGroups(manager) {
    const definitions = (manager.specDefinitions || [])
      .filter((definition) => definition.enabled !== false && definition.group?.enabled !== false && definition.key)
      .sort((left, right) => (
        Number(left.group?.order || 0) - Number(right.group?.order || 0)
        || Number(left.order || 0) - Number(right.order || 0)
      ));
    const groups = new Map();
    definitions.forEach((definition) => {
      const key = definition.group?.key || 'specifications';
      if (!groups.has(key)) groups.set(key, { ...definition.group, key, definitions: [] });
      groups.get(key).definitions.push(definition);
    });
    return Array.from(groups.values());
  }

  function renderSpecs(manager, products) {
    const container = manager.querySelector('[data-vl-product-compare-specs]');
    const navs = manager.querySelectorAll('[data-vl-product-compare-spec-nav]');
    if (!container || !navs.length) return;
    const groups = specGroups(manager);
    const slots = slotsFor(products);

    const navHtml = groups.filter((group) => group.name).map((group, index) => {
      const groupToken = domToken(group.key);
      return `<a href="#vl-compare-${groupToken}" data-vl-product-compare-nav data-group-key="${groupToken}" ${index === 0 ? 'class="is-active" aria-current="true"' : ''}>${escapeHtml(group.name)}</a>`;
    }).join('');
    navs.forEach((nav) => { nav.innerHTML = navHtml; });
    container.innerHTML = groups.map((group) => `
      <section class="vl-product-compare__spec-group${group.name ? '' : ' vl-product-compare__spec-group--unheaded'}" id="vl-compare-${domToken(group.key)}" data-group-key="${domToken(group.key)}" role="rowgroup">
        ${group.name ? `<h2>${escapeHtml(group.name)}</h2>` : ''}
        <div class="vl-product-compare__spec-group-scroll" data-vl-product-compare-scroll tabindex="0" aria-label="${escapeHtml(`${group.name || manager.labels?.specifications || 'Specifications'}. ${manager.labels?.horizontalScrollHint || ''}`.trim())}">
          <div class="vl-product-compare__spec-group-rows">
            ${group.definitions.map((definition) => {
              const presentValues = products.map((product) => normalizedSpecValue(product.specs?.[definition.key], definition, manager.labels || {}));
              const isDifferent = new Set(presentValues.map(comparableSpecValue)).size > 1;
              const isNumeric = Boolean(String(definition.unit || '').trim());
              return `
                <div class="vl-product-compare__spec-row${definition.icon ? ' vl-product-compare__spec-row--with-icon' : ''}${isDifferent ? ' is-different' : ''}" role="row">
                  <div class="vl-product-compare__spec-values">
                    ${slots.map((product) => `
                      <div class="vl-product-compare__spec-value${definition.icon ? ' vl-product-compare__spec-value--with-icon' : ''}${isNumeric ? ' vl-product-compare__spec-value--numeric' : ''}" role="cell">
                        ${definition.icon ? `<img class="vl-product-compare__spec-icon" src="${escapeHtml(definition.icon)}" alt="" loading="lazy">` : ''}
                        <span class="vl-product-compare__spec-field">${escapeHtml(definition.label || definition.key)}</span>
                        <span class="vl-product-compare__spec-data">${formatSpecValueHtml(product ? normalizedSpecValue(product.specs?.[definition.key], definition, manager.labels || {}) : '—')}</span>
                      </div>
                    `).join('')}
                  </div>
                </div>
              `;
            }).join('')}
          </div>
        </div>
      </section>
    `).join('');
  }

  function applyDifferenceFilter(manager) {
    const input = manager.querySelector('[data-vl-product-compare-differences]');
    const differencesOnly = Boolean(input?.checked);
    manager.classList.toggle('is-showing-differences', differencesOnly);
    manager.querySelectorAll('.vl-product-compare__spec-row').forEach((row) => {
      row.hidden = differencesOnly && !row.classList.contains('is-different');
    });
    manager.querySelectorAll('.vl-product-compare__spec-group').forEach((group) => {
      group.hidden = differencesOnly && !group.querySelector('.vl-product-compare__spec-row.is-different');
    });
    manager.querySelectorAll('[data-vl-product-compare-nav]').forEach((link) => {
      const group = manager.querySelector(`.vl-product-compare__spec-group[data-group-key="${link.dataset.groupKey}"]`);
      link.hidden = Boolean(group?.hidden);
    });
    const activeLink = manager.querySelector('[data-vl-product-compare-nav].is-active:not([hidden])');
    if (!activeLink) setActiveNav(manager, manager.querySelector('[data-vl-product-compare-nav]:not([hidden])')?.dataset.groupKey);
  }

  function navigableGroupKey(manager, groupKey) {
    if (!groupKey) return null;
    const navKeys = new Set(Array.from(
      manager.querySelectorAll('[data-vl-product-compare-nav]:not([hidden])')
    ).map((link) => link.dataset.groupKey));
    if (navKeys.has(groupKey)) return groupKey;

    const groups = Array.from(
      manager.querySelectorAll('.vl-product-compare__spec-group:not([hidden])')
    );
    const groupIndex = groups.findIndex((group) => group.dataset.groupKey === groupKey);
    if (groupIndex >= 0) {
      for (let index = groupIndex + 1; index < groups.length; index += 1) {
        if (navKeys.has(groups[index].dataset.groupKey)) return groups[index].dataset.groupKey;
      }
      for (let index = groupIndex - 1; index >= 0; index -= 1) {
        if (navKeys.has(groups[index].dataset.groupKey)) return groups[index].dataset.groupKey;
      }
    }

    return navKeys.values().next().value || null;
  }

  function setActiveNav(manager, groupKey) {
    const activeGroupKey = navigableGroupKey(manager, groupKey);
    if (!activeGroupKey) return;
    const navLinks = Array.from(manager.querySelectorAll('[data-vl-product-compare-nav]'));
    const activeNavIsCurrent = navLinks.length > 0 && navLinks.every((link) => (
      link.classList.contains('is-active') === (link.dataset.groupKey === activeGroupKey)
    ));
    if (activeNavIsCurrent) return;

    let activeHorizontalLink = null;
    navLinks.forEach((link) => {
      const active = link.dataset.groupKey === activeGroupKey;
      link.classList.toggle('is-active', active);
      if (active) link.setAttribute('aria-current', 'true');
      else link.removeAttribute('aria-current');
      if (active && link.closest('.vl-product-compare__spec-nav-mobile')) {
        activeHorizontalLink = link;
      }
    });

    const nav = activeHorizontalLink?.closest('.vl-product-compare__spec-nav-mobile');
    if (!nav || nav.scrollWidth <= nav.clientWidth) return;
    const targetLeft = activeHorizontalLink.offsetLeft
      - ((nav.clientWidth - activeHorizontalLink.offsetWidth) / 2);
    nav.scrollTo({
      left: Math.max(0, targetLeft),
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
    });
  }

  function specScrollRoot(manager) {
    if (!manager.classList.contains('vl-product-compare--pdp')) return null;
    return manager.closest('#compare_popup')?.querySelector('.popup__content-container') || null;
  }

  function clearSpecNavScrollLock(manager) {
    if (manager.specNavScrollRoot && manager.specNavScrollHandler) {
      manager.specNavScrollRoot.removeEventListener('scroll', manager.specNavScrollHandler);
    }
    window.clearTimeout(manager.specNavScrollEndTimer);
    window.clearTimeout(manager.specNavScrollFallbackTimer);
    delete manager.specNavLockedKey;
    delete manager.specNavScrollRoot;
    delete manager.specNavScrollHandler;
    delete manager.specNavScrollEndTimer;
    delete manager.specNavScrollFallbackTimer;
    delete manager.specNavScrollTarget;
    delete manager.specNavAlignmentAttempts;
  }

  function alignSpecGroup(manager, target, scrollRoot, behavior = 'auto') {
    const targetRect = target.getBoundingClientRect();
    const rootRect = scrollRoot?.getBoundingClientRect();
    const currentTop = targetRect.top - (rootRect?.top || 0);
    const expectedTop = Number.parseFloat(window.getComputedStyle(target).scrollMarginTop) || 0;
    const delta = currentTop - expectedTop;
    if (Math.abs(delta) <= 1) return delta;

    if (scrollRoot) {
      scrollRoot.scrollTo({ top: scrollRoot.scrollTop + delta, behavior });
    } else {
      window.scrollTo({ top: window.scrollY + delta, behavior });
    }
    return delta;
  }

  function finishSpecNavScroll(manager) {
    const target = manager.specNavScrollTarget;
    if (target?.isConnected && manager.specNavLockedKey) {
      const delta = alignSpecGroup(manager, target, specScrollRoot(manager));
      manager.specNavAlignmentAttempts = (manager.specNavAlignmentAttempts || 0) + 1;
      if (Math.abs(delta) > 2 && manager.specNavAlignmentAttempts < 3) {
        window.clearTimeout(manager.specNavScrollEndTimer);
        manager.specNavScrollEndTimer = window.setTimeout(() => finishSpecNavScroll(manager), 120);
        return;
      }
    }
    clearSpecNavScrollLock(manager);
  }

  function lockSpecNavDuringScroll(manager, groupKey, scrollRoot, target) {
    clearSpecNavScrollLock(manager);
    manager.specNavLockedKey = groupKey;
    manager.specNavScrollRoot = scrollRoot || window;
    manager.specNavScrollTarget = target;
    manager.specNavAlignmentAttempts = 0;
    manager.specNavScrollHandler = () => {
      window.clearTimeout(manager.specNavScrollEndTimer);
      manager.specNavScrollEndTimer = window.setTimeout(() => finishSpecNavScroll(manager), 180);
    };
    manager.specNavScrollRoot.addEventListener('scroll', manager.specNavScrollHandler, { passive: true });
    manager.specNavScrollEndTimer = window.setTimeout(() => finishSpecNavScroll(manager), 220);
    manager.specNavScrollFallbackTimer = window.setTimeout(() => finishSpecNavScroll(manager), 2400);
  }

  function scrollToSpecGroup(manager, groupKey) {
    const target = manager.querySelector(`.vl-product-compare__spec-group[data-group-key="${groupKey}"]`);
    if (!target) return;
    const scrollRoot = specScrollRoot(manager);
    const behavior = window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

    lockSpecNavDuringScroll(manager, groupKey, scrollRoot, target);
    setActiveNav(manager, groupKey);
    alignSpecGroup(manager, target, scrollRoot, behavior);
  }

  function currentSpecGroup(manager, scrollRoot) {
    const groups = Array.from(manager.querySelectorAll('.vl-product-compare__spec-group:not([hidden])'));
    if (!groups.length) return null;
    const scrollTop = scrollRoot ? scrollRoot.scrollTop : window.scrollY;
    const viewportHeight = scrollRoot ? scrollRoot.clientHeight : window.innerHeight;
    const scrollHeight = scrollRoot ? scrollRoot.scrollHeight : document.documentElement.scrollHeight;
    const remainingScroll = Math.max(0, scrollHeight - viewportHeight - scrollTop);
    const enterEndThreshold = 2;
    const leaveEndThreshold = 32;
    if (remainingScroll <= enterEndThreshold) manager.specNavAtEnd = true;
    else if (remainingScroll > leaveEndThreshold) manager.specNavAtEnd = false;
    if (manager.specNavAtEnd) return groups.at(-1);

    const rootRect = scrollRoot?.getBoundingClientRect();
    const rootTop = rootRect?.top || 0;
    const rootHeight = rootRect?.height || window.innerHeight;
    const activationLine = rootTop + (rootHeight * 0.2);
    const reachedGroups = groups.filter((group) => group.getBoundingClientRect().top <= activationLine);
    if (reachedGroups.length) return reachedGroups.at(-1);
    return groups[0];
  }

  function observeSpecGroups(manager) {
    manager.specNavObserver?.disconnect();
    manager.specNavTrackingTarget?.removeEventListener('scroll', manager.specNavTrackingHandler);
    if (manager.specNavTrackingFrame != null) window.cancelAnimationFrame(manager.specNavTrackingFrame);
    manager.specNavAtEnd = false;
    const scrollRoot = specScrollRoot(manager);
    manager.specNavTrackingTarget = scrollRoot || window;
    manager.specNavTrackingHandler = () => {
      if (manager.specNavTrackingFrame != null) return;
      manager.specNavTrackingFrame = window.requestAnimationFrame(() => {
        manager.specNavTrackingFrame = null;
        if (manager.specNavLockedKey) return;
        const activeGroup = currentSpecGroup(manager, scrollRoot);
        if (activeGroup) setActiveNav(manager, activeGroup.dataset.groupKey);
      });
    };
    manager.specNavTrackingTarget.addEventListener('scroll', manager.specNavTrackingHandler, { passive: true });
    manager.specNavObserver = new IntersectionObserver(() => {
      manager.specNavTrackingHandler();
    }, {
      root: scrollRoot,
      rootMargin: '-20% 0px -65% 0px'
    });
    manager.querySelectorAll('.vl-product-compare__spec-group').forEach((group) => manager.specNavObserver.observe(group));
  }

  function renderCandidates(manager, candidates, slotIndex) {
    const container = manager.querySelector('[data-vl-product-compare-candidates]');
    if (!container) return;
    const selectedHandles = new Set(manager.products.map((product) => product.handle));
    const groups = new Map();

    candidates.forEach((candidate) => {
      const rawCategory = candidate.category;
      const category = rawCategory && typeof rawCategory === 'object'
        ? rawCategory
        : { key: rawCategory || 'other', name: rawCategory || 'Other', order: 999, enabled: true };
      if (category.enabled === false) return;
      const categoryKey = String(category.key || category.name || 'other');
      if (!groups.has(categoryKey)) {
        groups.set(categoryKey, {
          key: categoryKey,
          name: category.name || 'Other',
          order: Number.isFinite(Number(category.order)) ? Number(category.order) : 999,
          products: []
        });
      }
      groups.get(categoryKey).products.push(candidate);
    });

    const sortedGroups = Array.from(groups.values())
      .sort((left, right) => left.order - right.order || left.name.localeCompare(right.name));
    sortedGroups.forEach((group) => {
      group.products.sort((left, right) => (
        (Number.isFinite(Number(left.selectorOrder)) ? Number(left.selectorOrder) : 999)
        - (Number.isFinite(Number(right.selectorOrder)) ? Number(right.selectorOrder) : 999)
        || String(left.title || '').localeCompare(String(right.title || ''))
      ));
    });

    container.innerHTML = sortedGroups.map((group) => `
      <details class="vl-product-compare__candidate-group" open>
        <summary>
          <span>${escapeHtml(group.name)}</span>
          <span class="vl-product-compare__candidate-chevron" aria-hidden="true">
            <svg class="vl-product-compare__chevron-down" width="18" height="18" viewBox="0 0 20 20" fill="none">
              <path d="M9.71886 13.702C9.88158 13.8647 10.1453 13.8647 10.3081 13.702L17.0846 6.92546C17.2472 6.76275 17.2472 6.49895 17.0846 6.33627L16.4954 5.74708C16.3327 5.5844 16.0689 5.58448 15.9062 5.74708L10.0135 11.6398L4.12071 5.74708C3.95799 5.5844 3.69422 5.58437 3.53152 5.74708L2.94233 6.33627C2.77963 6.49897 2.77966 6.76274 2.94233 6.92546L9.71886 13.702Z" fill="#1F1F1F"/>
            </svg>
            <svg class="vl-product-compare__chevron-up" width="18" height="18" viewBox="0 0 18 18" fill="none">
              <path d="M8.74775 5.17238C8.8942 5.02597 9.13159 5.02594 9.27803 5.17238L15.3769 11.2713C15.5232 11.4177 15.5233 11.6551 15.3769 11.8015L14.8466 12.3318C14.7002 12.4782 14.4628 12.4781 14.3164 12.3318L9.01289 7.02833L3.70942 12.3318C3.56297 12.4782 3.32558 12.4782 3.17915 12.3318L2.64888 11.8015C2.50244 11.6551 2.50247 11.4177 2.64888 11.2713L8.74775 5.17238Z" fill="#1F1F1F"/>
            </svg>
          </span>
        </summary>
        <div class="vl-product-compare__candidate-grid">
          ${group.products.map((candidate) => {
            const selected = selectedHandles.has(candidate.handle);
            return `
              <button type="button" class="vl-product-compare__candidate${selected ? ' is-selected' : ''}"
                data-vl-product-compare-candidate data-handle="${escapeHtml(candidate.handle)}" data-slot-index="${slotIndex}"
                ${selected ? 'disabled aria-disabled="true"' : ''}>
                ${candidate.subtitle ? `<span class="vl-product-compare__candidate-subtitle">${escapeHtml(candidate.subtitle)}</span>` : ''}
                <span class="vl-product-compare__candidate-image">
                  ${candidate.image ? `<img src="${escapeHtml(candidate.imageSmall || candidate.image)}"${candidate.imageSmall ? ` srcset="${escapeHtml(candidate.imageSmall)} 1x, ${escapeHtml(candidate.image)} 2x"` : ''} alt="" loading="lazy" decoding="async">` : ''}
                </span>
                <span class="vl-product-compare__candidate-title">${escapeHtml(candidate.title)}</span>
                ${selected ? `<span class="vl-product-compare__candidate-status">${escapeHtml(manager.labels?.selected || 'Selected')}</span>` : ''}
                ${candidate.available === false ? `<span class="vl-product-compare__candidate-status">${escapeHtml(manager.labels?.unavailable || 'Unavailable')}</span>` : ''}
              </button>
            `;
          }).join('')}
        </div>
      </details>
    `).join('');
  }

  function syncScrollAreas(manager) {
    const areas = Array.from(manager.querySelectorAll('[data-vl-product-compare-scroll]'));
    manager.vlScrollAreas = areas;

    if (!manager.vlScrollResizeBound) {
      manager.vlScrollResizeBound = true;
      window.addEventListener('resize', () => {
        window.clearTimeout(manager.vlScrollResizeTimer);
        manager.vlScrollResizeTimer = window.setTimeout(() => syncScrollAreas(manager), 120);
      }, { signal: manager.abortController?.signal });
    }

    const mobileLayout = window.matchMedia('(max-width: 1079px)').matches;
    areas.forEach((area) => {
      if (mobileLayout) {
        if (area.dataset.vlScrollAriaLabel) area.setAttribute('aria-label', area.dataset.vlScrollAriaLabel);
        if (!area.matches('.vl-product-compare__sticky-actions-scroll')) area.tabIndex = 0;
      } else {
        const ariaLabel = area.getAttribute('aria-label');
        if (ariaLabel) area.dataset.vlScrollAriaLabel = ariaLabel;
        area.removeAttribute('aria-label');
        area.removeAttribute('tabindex');
        area.scrollLeft = 0;
      }
    });

    manager.vlScrollVisibilityObserver?.disconnect();
    manager.vlScrollVisibilityObserver = null;
    if (mobileLayout && typeof IntersectionObserver === 'function') {
      manager.vlScrollVisibilityObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting || entry.target.hidden) return;
          if (Math.abs(entry.target.scrollLeft - manager.vlScrollSyncLeft) <= 0.5) return;
          entry.target.vlExpectedScrollLeft = manager.vlScrollSyncLeft;
          entry.target.scrollLeft = manager.vlScrollSyncLeft;
        });
      });
      areas.forEach((area) => manager.vlScrollVisibilityObserver.observe(area));
    }

    const shouldSyncArea = (area) => {
      if (area.hidden) return false;
      if (area.matches('.vl-product-compare__selectors-scroll, .vl-product-compare__sticky-actions-scroll')) return true;
      const rect = area.getBoundingClientRect();
      return rect.bottom > 0 && rect.top < window.innerHeight;
    };

    const wheelDelta = (event, value) => {
      if (event.deltaMode === WheelEvent.DOM_DELTA_LINE) return value * 16;
      if (event.deltaMode === WheelEvent.DOM_DELTA_PAGE) return value * window.innerHeight;
      return value;
    };

    const handleWheel = (area, event) => {
      if (!window.matchMedia('(max-width: 1079px)').matches || event.ctrlKey || !event.cancelable) return;
      const deltaX = wheelDelta(event, event.deltaX);
      const deltaY = wheelDelta(event, event.deltaY);
      if (!deltaX && !deltaY) return;

      if (!manager.vlWheelAxis) {
        manager.vlWheelAxis = Math.abs(deltaX) > Math.abs(deltaY) ? 'x' : 'y';
      }
      window.clearTimeout(manager.vlWheelAxisTimer);
      manager.vlWheelAxisTimer = window.setTimeout(() => { manager.vlWheelAxis = null; }, 140);
      event.preventDefault();

      if (manager.vlWheelAxis === 'x') {
        area.scrollLeft += deltaX || deltaY;
        return;
      }

      const popupScroller = manager.mode === 'pdp'
        ? manager.closest('#compare_popup')?.querySelector('.popup__content-container')
        : null;
      if (popupScroller) popupScroller.scrollTop += deltaY;
      else window.scrollBy({ top: deltaY, left: 0, behavior: 'auto' });
    };

    if (!mobileLayout) return;

    areas.forEach((area) => {
      if (area.dataset.vlScrollSync === 'true') return;
      area.dataset.vlScrollSync = 'true';
      area.addEventListener('wheel', (event) => handleWheel(area, event), { passive: false });
      area.addEventListener('scroll', () => {
        if (!window.matchMedia('(max-width: 1079px)').matches) return;
        if (
          area !== manager.vlScrollSyncSource
          && area.vlExpectedScrollLeft != null
          && Math.abs(area.scrollLeft - area.vlExpectedScrollLeft) <= 0.5
        ) {
          area.vlExpectedScrollLeft = null;
          return;
        }
        area.vlExpectedScrollLeft = null;
        manager.vlScrollSyncSource = area;
        manager.vlScrollSyncLeft = area.scrollLeft;
        if (manager.vlScrollSyncFrame != null) return;
        manager.vlScrollSyncFrame = window.requestAnimationFrame(() => {
          const source = manager.vlScrollSyncSource;
          const scrollLeft = manager.vlScrollSyncLeft;
          manager.vlScrollSyncFrame = null;
          (manager.vlScrollAreas || []).forEach((other) => {
            if (!other.isConnected) return;
            if (other !== source && shouldSyncArea(other) && Math.abs(other.scrollLeft - scrollLeft) > 0.5) {
              other.vlExpectedScrollLeft = scrollLeft;
              other.scrollLeft = scrollLeft;
            }
          });
        });
      }, { passive: true });
    });
  }

  function refreshAffirm() {
    const affirm = window.affirm;
    if (!affirm?.ui) return;
    const refresh = () => {
      if (typeof affirm.ui.refresh === 'function') affirm.ui.refresh();
    };
    try {
      if (typeof affirm.ui.ready === 'function') affirm.ui.ready(refresh);
      else refresh();
    } catch (error) {
      // Keep the comparison usable when the optional financing integration is unavailable.
    }
  }

  function lockSelectorPageScroll(manager, dialog) {
    if (!window.matchMedia('(max-width: 1079px)').matches || manager.selectorScrollLock) return;
    const body = document.body;
    const root = document.documentElement;
    const scrollY = window.scrollY;
    manager.selectorScrollLock = {
      scrollY,
      rootOverflow: root.style.overflow,
      bodyOverflow: body.style.overflow,
      bodyPosition: body.style.position,
      bodyTop: body.style.top,
      bodyLeft: body.style.left,
      bodyRight: body.style.right,
      bodyWidth: body.style.width
    };
    root.classList.add('vl-product-compare-dialog-open');
    body.classList.add('vl-product-compare-dialog-open');
    root.style.overflow = 'hidden';
    body.style.overflow = 'hidden';
    body.style.position = 'fixed';
    body.style.top = `${-scrollY}px`;
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    dialog.dataset.vlPageScrollLocked = 'true';
  }

  function unlockSelectorPageScroll(manager, dialog) {
    const lock = manager.selectorScrollLock;
    if (!lock) return;
    const body = document.body;
    const root = document.documentElement;
    root.classList.remove('vl-product-compare-dialog-open');
    body.classList.remove('vl-product-compare-dialog-open');
    root.style.overflow = lock.rootOverflow;
    body.style.overflow = lock.bodyOverflow;
    body.style.position = lock.bodyPosition;
    body.style.top = lock.bodyTop;
    body.style.left = lock.bodyLeft;
    body.style.right = lock.bodyRight;
    body.style.width = lock.bodyWidth;
    delete dialog.dataset.vlPageScrollLocked;
    delete manager.selectorScrollLock;
    window.scrollTo({ top: lock.scrollY, left: 0, behavior: 'auto' });
  }

  async function openSelector(manager, slotIndex, trigger) {
    const dialog = manager.querySelector('[data-vl-product-compare-dialog]');
    if (!dialog) return;
    manager.selectorTrigger = trigger;
    manager.selectorSlotIndex = slotIndex;
    dialog.setAttribute('aria-busy', 'true');
    const container = manager.querySelector('[data-vl-product-compare-candidates]');
    if (container && !manager.candidates) {
      container.innerHTML = `<div class="vl-product-compare__candidate-loading" role="status">
        <span class="vl-product-compare__candidate-spinner" aria-hidden="true"></span>
        <span>${escapeHtml(manager.labels?.loading || 'Loading ebikes...')}</span>
      </div>`;
    }

    if (window.matchMedia('(min-width: 1080px)').matches) {
      const rect = trigger.getBoundingClientRect();
      const dialogWidth = 704;
      const dialogHeight = 616;
      const viewportGap = 16;
      const left = Math.max(viewportGap, Math.min(rect.left, window.innerWidth - dialogWidth - viewportGap));
      const top = Math.max(viewportGap, Math.min(rect.bottom + 8, window.innerHeight - dialogHeight - viewportGap));
      dialog.style.setProperty('--vl-compare-dialog-left', `${left}px`);
      dialog.style.setProperty('--vl-compare-dialog-top', `${top}px`);
    } else {
      dialog.style.removeProperty('--vl-compare-dialog-left');
      dialog.style.removeProperty('--vl-compare-dialog-top');
    }
    if (!dialog.open) dialog.showModal();
    lockSelectorPageScroll(manager, dialog);

    try {
      const candidates = await manager.getCandidates();
      if (!dialog.open || manager.selectorSlotIndex !== slotIndex) return;
      renderCandidates(manager, candidates, slotIndex);
      dialog.querySelector('button:not([disabled])')?.focus();
    } catch (error) {
      if (dialog.open) dialog.close();
      manager.emitError('candidate-load-failed', error);
    } finally {
      dialog.removeAttribute('aria-busy');
    }
  }

  function mount(manager) {
    if (manager.dataset.vlCompareViewMounted === 'true') return;
    manager.dataset.vlCompareViewMounted = 'true';
    const status = manager.querySelector('[data-vl-product-compare-status]');
    const content = manager.querySelector('[data-vl-product-compare-content]');
    const error = manager.querySelector('[data-vl-product-compare-error]');
    const live = manager.querySelector('[data-vl-product-compare-live]');
    const dialog = manager.querySelector('[data-vl-product-compare-dialog]');

    manager.addEventListener('vl:product-compare-change', (event) => {
      if (event.detail.source === 'variant-option' && event.detail.productId) {
        const update = updateVariantProduct(
          manager,
          event.detail.products || [],
          event.detail.productId,
          event.detail.optionField
        );
        if (update.updated) {
          if (update.affirmChanged) refreshAffirm();
          if (status) status.hidden = true;
          if (error) error.hidden = true;
          if (content) content.hidden = false;
          return;
        }
      }

      renderProducts(manager, event.detail.products || []);
      renderSpecs(manager, event.detail.products || []);
      applyDifferenceFilter(manager);
      observeSpecGroups(manager);
      syncScrollAreas(manager);
      refreshAffirm();
      if (status) status.hidden = true;
      if (error) error.hidden = true;
      if (content) content.hidden = false;
      if (live) live.textContent = String(manager.labels?.productsReady || '{count} products ready for comparison.').replace('{count}', event.detail.products.length);
      manager.trackRenderedView();
      if (event.detail.source === 'replace-product') {
        manager.trackAnalytics('compare_product_change', {
          previousProductHandle: event.detail.previousProductHandle,
          operatedProductHandle: event.detail.operatedProductHandle,
          operatedSlot: event.detail.operatedSlot,
          action: 'replace'
        });
      }
    });

    manager.addEventListener('vl:product-compare-error', (event) => {
      if (event.detail?.code === 'candidate-load-failed') return;
      if (status) status.hidden = true;
      if (content) content.hidden = true;
      if (error) error.hidden = false;
    });

    manager.addEventListener('change', (event) => {
      const input = event.target.closest('[data-vl-product-compare-option]');
      if (input) {
        manager.selectVariantOption(input.dataset.productId, input.dataset.optionField, input.value);
        return;
      }
      if (event.target.matches('[data-vl-product-compare-differences]')) {
        applyDifferenceFilter(manager);
        manager.trackAnalytics('compare_difference_toggle', {
          action: event.target.checked ? 'enable' : 'disable'
        });
        if (live) {
          live.textContent = event.target.checked
            ? (manager.labels?.differencesShown || 'Only differences are shown.')
            : (manager.labels?.allSpecificationsShown || 'All specifications are shown.');
        }
      }
    });

    manager.addEventListener('error', (event) => {
      const image = event.target.closest('img');
      if (!image) return;
      image.hidden = true;
      image.closest('.vl-product-compare__image-frame')?.classList.add('has-image-error');
    }, true);

    manager.addEventListener('click', async (event) => {
      const productLink = event.target.closest(
        '.vl-product-compare__explore, .vl-product-compare__sticky-explore, .vl-product-compare__image-link'
      );
      if (productLink) {
        const selector = productLink.matches('.vl-product-compare__sticky-explore')
          ? '.vl-product-compare__sticky-explore'
          : '.vl-product-compare__explore';
        const productElement = productLink.closest('.vl-product-compare__product');
        const operatedSlot = productElement
          ? Array.from(productElement.parentElement.children).indexOf(productElement) + 1
          : Array.from(manager.querySelectorAll(selector)).indexOf(productLink) + 1;
        const product = manager.products[operatedSlot - 1];
        if (product) {
          manager.trackAnalytics('compare_product_click', {
            operatedProductHandle: product.handle,
            operatedSlot,
            action: productLink.matches('.vl-product-compare__image-link') ? 'image' : 'explore'
          });
        }
      }
      const selector = event.target.closest('[data-vl-product-compare-selector]');
      if (selector) {
        await openSelector(manager, Number(selector.dataset.slotIndex), selector);
        return;
      }
      const candidate = event.target.closest('[data-vl-product-compare-candidate]');
      if (candidate) {
        if (dialog?.hasAttribute('aria-busy')) return;
        dialog?.setAttribute('aria-busy', 'true');
        candidate.classList.add('is-loading');
        candidate.setAttribute('aria-disabled', 'true');
        const replaced = await manager.replaceProduct(Number(candidate.dataset.slotIndex), candidate.dataset.handle);
        if (replaced) dialog?.close();
        else {
          dialog?.removeAttribute('aria-busy');
          candidate.classList.remove('is-loading');
          candidate.removeAttribute('aria-disabled');
        }
        return;
      }
      if (event.target.closest('[data-vl-product-compare-dialog-close]')) dialog?.close();
      if (event.target.closest('[data-vl-product-compare-retry]')) manager.initialize();
      const navLink = event.target.closest('[data-vl-product-compare-nav]');
      if (navLink) {
        event.preventDefault();
        scrollToSpecGroup(manager, navLink.dataset.groupKey);
      }
    });

    dialog?.addEventListener('close', () => {
      unlockSelectorPageScroll(manager, dialog);
      dialog.style.removeProperty('--vl-compare-dialog-left');
      dialog.style.removeProperty('--vl-compare-dialog-top');
      if (manager.selectorTrigger?.isConnected) manager.selectorTrigger.focus();
    });
    dialog?.addEventListener('click', (event) => { if (event.target === dialog) dialog.close(); });

    if (Array.isArray(manager.products) && manager.products.length) {
      renderProducts(manager, manager.products);
      renderSpecs(manager, manager.products);
      applyDifferenceFilter(manager);
      observeSpecGroups(manager);
      syncScrollAreas(manager);
      refreshAffirm();
      if (status) status.hidden = true;
      if (error) error.hidden = true;
      if (content) content.hidden = false;
      manager.trackRenderedView();
    }
  }

  function scan(root = document) {
    root.querySelectorAll?.(MANAGER_SELECTOR).forEach(mount);
  }

  scan();
  new MutationObserver((mutations) => {
    mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
      if (node.nodeType !== Node.ELEMENT_NODE) return;
      if (node.matches?.(MANAGER_SELECTOR)) mount(node);
      scan(node);
    }));
  }).observe(document.documentElement, { childList: true, subtree: true });
})();
